"use client";

import { c as _c } from "react/compiler-runtime";
import NFTImage from "@/components/nft-image/NFTImage";
import NFTImageBalance from "@/components/nft-image/NFTImageBalance";
import { InlineMediaActions } from "@/components/drops/view/item/content/media/MediaActionToolbar";
import { useMediaActions } from "@/components/drops/view/item/content/media/useMediaActions";
import { useAuth } from "@/components/auth/Auth";
import { getResolvedAnimationSrc } from "@/components/nft-image/utils/animation-source";
import { getResolvedImageSrc } from "@/components/nft-image/utils/image-source";
import type { NFT } from "@/entities/INFT";
import { enterArtFullScreen } from "@/helpers/Helpers";
import { getAnimationFileTypeFromMetadata, getAnimationMimeTypeFromMetadata, getImageFileTypeFromMetadata, getImageMimeTypeFromMetadata } from "@/helpers/nft.helpers";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import { Carousel, Col, Container, Row } from "react-bootstrap";
import { flushSync } from "react-dom";
import styles from "./TheMemes.module.scss";
type InlineMediaVariant = "image" | "html";
const FULLSCREEN_CHANGE_EVENTS = ["fullscreenchange", "webkitfullscreenchange", "mozfullscreenchange", "MSFullscreenChange"] as const;
const FULLSCREEN_ERROR_EVENTS = ["fullscreenerror", "webkitfullscreenerror", "mozfullscreenerror", "MSFullscreenError"] as const;
type FullscreenDocument = Document & {
  readonly webkitFullscreenElement?: Element | null;
  readonly mozFullScreenElement?: Element | null;
  readonly msFullscreenElement?: Element | null;
};
function getCurrentFullscreenElement(doc: Document): Element | null {
  const fullscreenDocument = doc as FullscreenDocument;
  return fullscreenDocument.fullscreenElement ?? fullscreenDocument.webkitFullscreenElement ?? fullscreenDocument.mozFullScreenElement ?? fullscreenDocument.msFullscreenElement ?? null;
}
function getInlineMediaVariant(mimeType: string | null | undefined): InlineMediaVariant {
  const normalizedMimeType = (mimeType?.split(";")[0] ?? "").trim().toLowerCase();
  if (normalizedMimeType === "text/html") {
    return "html";
  }
  return "image";
}
export function MemePageArtViewer(t0) {
  const $ = _c(55);
  const {
    nft,
    showBalance: t1
  } = t0;
  const showBalance = t1 === undefined ? false : t1;
  const {
    connectedProfile
  } = useAuth();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [fullscreenOriginalTargetId, setFullscreenOriginalTargetId] = useState(null);
  let t2;
  if ($[0] !== nft) {
    t2 = getResolvedAnimationSrc(nft);
    $[0] = nft;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const animationHref = t2;
  const hasAnimation = Boolean(animationHref);
  const metadata = nft.metadata as Parameters<typeof getImageFileTypeFromMetadata>[0];
  let t3;
  if ($[2] !== metadata) {
    t3 = getImageFileTypeFromMetadata(metadata);
    $[2] = metadata;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const imageFormat = t3;
  let t4;
  if ($[4] !== metadata) {
    t4 = getAnimationFileTypeFromMetadata(metadata);
    $[4] = metadata;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const animationFormat = t4;
  let hasImage;
  let hasMultipleSlides;
  let t5;
  if ($[6] !== animationFormat || $[7] !== animationHref || $[8] !== currentSlide || $[9] !== hasAnimation || $[10] !== imageFormat || $[11] !== metadata || $[12] !== nft) {
    const imageMimeType = getImageMimeTypeFromMetadata(metadata);
    const animationMimeType = getAnimationMimeTypeFromMetadata(metadata);
    let t6;
    if ($[16] !== nft) {
      t6 = getResolvedImageSrc(nft);
      $[16] = nft;
      $[17] = t6;
    } else {
      t6 = $[17];
    }
    const imageHref = t6;
    hasImage = Boolean(imageHref);
    const isShowingAnimation = hasAnimation && (currentSlide === 0 || !imageHref);
    hasMultipleSlides = hasAnimation && hasImage;
    t5 = isShowingAnimation ? {
      dialogTitle: "Save animation",
      fallbackFileName: "animation",
      format: animationFormat,
      fullscreenElementId: "the-art-fullscreen-animation",
      mimeType: animationMimeType,
      url: animationHref,
      variant: getInlineMediaVariant(animationMimeType)
    } : {
      dialogTitle: "Save image",
      fallbackFileName: "image",
      format: imageFormat,
      fullscreenElementId: hasImage ? "the-art-fullscreen-img" : "",
      mimeType: imageMimeType,
      url: imageHref,
      variant: getInlineMediaVariant(imageMimeType)
    };
    $[6] = animationFormat;
    $[7] = animationHref;
    $[8] = currentSlide;
    $[9] = hasAnimation;
    $[10] = imageFormat;
    $[11] = metadata;
    $[12] = nft;
    $[13] = hasImage;
    $[14] = hasMultipleSlides;
    $[15] = t5;
  } else {
    hasImage = $[13];
    hasMultipleSlides = $[14];
    t5 = $[15];
  }
  const activeMedia = t5;
  const currentFormat = activeMedia.format ?? "";
  const activeMediaUrl = activeMedia.url ?? "";
  const canUseBrowserMediaActions = activeMedia.variant !== "html";
  const showBalanceControl = showBalance && Boolean(connectedProfile);
  const t6 = activeMedia.mimeType ?? undefined;
  let t7;
  if ($[18] !== activeMedia.dialogTitle || $[19] !== activeMedia.fallbackFileName || $[20] !== activeMediaUrl || $[21] !== t6) {
    t7 = {
      url: activeMediaUrl,
      fallbackFileName: activeMedia.fallbackFileName,
      dialogTitle: activeMedia.dialogTitle,
      mimeType: t6
    };
    $[18] = activeMedia.dialogTitle;
    $[19] = activeMedia.fallbackFileName;
    $[20] = activeMediaUrl;
    $[21] = t6;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  const {
    downloadMedia,
    isDownloading,
    openLabel,
    openMedia
  } = useMediaActions(t7);
  let t8;
  let t9;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      if (typeof globalThis.document === "undefined") {
        return;
      }
      const doc = globalThis.document;
      const clearOriginalAfterFullscreenExit = function clearOriginalAfterFullscreenExit() {
        if (!getCurrentFullscreenElement(doc)) {
          setFullscreenOriginalTargetId(null);
        }
      };
      FULLSCREEN_CHANGE_EVENTS.forEach(eventName => {
        doc.addEventListener(eventName, clearOriginalAfterFullscreenExit);
      });
      FULLSCREEN_ERROR_EVENTS.forEach(eventName_0 => {
        doc.addEventListener(eventName_0, clearOriginalAfterFullscreenExit);
      });
      return () => {
        FULLSCREEN_CHANGE_EVENTS.forEach(eventName_1 => {
          doc.removeEventListener(eventName_1, clearOriginalAfterFullscreenExit);
        });
        FULLSCREEN_ERROR_EVENTS.forEach(eventName_2 => {
          doc.removeEventListener(eventName_2, clearOriginalAfterFullscreenExit);
        });
      };
    };
    t9 = [];
    $[23] = t8;
    $[24] = t9;
  } else {
    t8 = $[23];
    t9 = $[24];
  }
  useEffect(t8, t9);
  let t10;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = function carouselHandlerSlide(event) {
      setCurrentSlide(event);
    };
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  const carouselHandlerSlide = t10;
  let t11;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = function goToPreviousSlide() {
      setCurrentSlide(_temp);
    };
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  const goToPreviousSlide = t11;
  let t12;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = function goToNextSlide() {
      setCurrentSlide(_temp2);
    };
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  const goToNextSlide = t12;
  let t13;
  if ($[28] !== activeMedia.fullscreenElementId) {
    t13 = function enterActiveMediaFullScreen() {
      const fullscreenElementId = activeMedia.fullscreenElementId;
      if (!fullscreenElementId) {
        return;
      }
      flushSync(() => {
        setFullscreenOriginalTargetId(fullscreenElementId);
      });
      const clearAttemptedOriginalTarget = () => {
        setFullscreenOriginalTargetId(currentTargetId => currentTargetId === fullscreenElementId ? null : currentTargetId);
      };
      (async () => {
        const didEnterFullscreen = await enterArtFullScreen(fullscreenElementId);
        if (!didEnterFullscreen) {
          clearAttemptedOriginalTarget();
        }
      })().catch(clearAttemptedOriginalTarget);
    };
    $[28] = activeMedia.fullscreenElementId;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  const enterActiveMediaFullScreen = t13;
  let t14;
  if ($[30] !== activeMedia.fullscreenElementId || $[31] !== activeMedia.variant || $[32] !== activeMediaUrl || $[33] !== canUseBrowserMediaActions || $[34] !== downloadMedia || $[35] !== enterActiveMediaFullScreen || $[36] !== isDownloading || $[37] !== openLabel || $[38] !== openMedia) {
    t14 = function printMediaActions() {
      if (!activeMediaUrl) {
        return null;
      }
      return <InlineMediaActions variant={activeMedia.variant} onDownload={canUseBrowserMediaActions ? downloadMedia : undefined} onOpen={canUseBrowserMediaActions ? openMedia : undefined} openLabel={canUseBrowserMediaActions ? openLabel : undefined} isDownloading={isDownloading} onFullscreen={enterActiveMediaFullScreen} fullscreenTargetAvailable={Boolean(activeMedia.fullscreenElementId)} />;
    };
    $[30] = activeMedia.fullscreenElementId;
    $[31] = activeMedia.variant;
    $[32] = activeMediaUrl;
    $[33] = canUseBrowserMediaActions;
    $[34] = downloadMedia;
    $[35] = enterActiveMediaFullScreen;
    $[36] = isDownloading;
    $[37] = openLabel;
    $[38] = openMedia;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  const printMediaActions = t14;
  let t15;
  if ($[40] !== currentFormat || $[41] !== currentSlide || $[42] !== hasMultipleSlides || $[43] !== nft.contract || $[44] !== nft.id || $[45] !== showBalanceControl) {
    t15 = function printArtworkControls() {
      if (!showBalanceControl && !hasMultipleSlides) {
        return null;
      }
      return <Col xs={12} className={styles.artControls}><div className={styles.artControlsContent}>{showBalanceControl && <div className={styles.artControlsBalance ?? ""}><NFTImageBalance contract={nft.contract} tokenId={nft.id} height={650} variant="compact" /></div>}{hasMultipleSlides && <div className={`${styles.artControlsCenter ?? ""} ${showBalanceControl ? styles.artControlsCenterWithBalance ?? "" : ""} ${styles.artControlsCenterGrouped ?? ""}`}><button type="button" aria-label="Show previous artwork media" disabled={currentSlide === 0} className={styles.artSlideButton} onClick={goToPreviousSlide}><ChevronLeftIcon aria-hidden="true" className={styles.artSlideIcon} /></button><div className={styles.artControlsFormatLabel}>{currentFormat}</div><button type="button" aria-label="Show next artwork media" disabled={currentSlide === 1} className={styles.artSlideButton} onClick={goToNextSlide}><ChevronRightIcon aria-hidden="true" className={styles.artSlideIcon} /></button></div>}</div></Col>;
    };
    $[40] = currentFormat;
    $[41] = currentSlide;
    $[42] = hasMultipleSlides;
    $[43] = nft.contract;
    $[44] = nft.id;
    $[45] = showBalanceControl;
    $[46] = t15;
  } else {
    t15 = $[46];
  }
  const printArtworkControls = t15;
  let t16;
  if ($[47] !== currentSlide || $[48] !== fullscreenOriginalTargetId || $[49] !== hasAnimation || $[50] !== hasImage || $[51] !== nft || $[52] !== printArtworkControls || $[53] !== printMediaActions) {
    t16 = <Container className="p-0 tw-flex tw-h-full tw-flex-col"><Row className="g-0 tw-flex-1 tw-flex-col">{hasAnimation ? <><div className="tw-flex tw-min-h-0 tw-w-full tw-flex-1 tw-items-center tw-bg-iron-950 tw-p-0"><Carousel className={`${styles.memesCarousel ?? ""} tw-w-full`} activeIndex={currentSlide} controls={false} interval={null} indicators={false} wrap={false} onSelect={carouselHandlerSlide}><Carousel.Item className="text-center"><NFTImage nft={nft} animation={true} height={650} transparentBG={true} showBalance={false} showOriginal={fullscreenOriginalTargetId === "the-art-fullscreen-animation"} id="the-art-fullscreen-animation" /></Carousel.Item>{hasImage && <Carousel.Item className="text-center"><NFTImage nft={nft} animation={false} height={650} showBalance={false} showOriginal={fullscreenOriginalTargetId === "the-art-fullscreen-img"} transparentBG={true} id="the-art-fullscreen-img" /></Carousel.Item>}</Carousel>{printMediaActions()}</div>{printArtworkControls()}</> : <>{hasImage && <div className="tw-flex tw-min-h-0 tw-w-full tw-flex-1 tw-items-center tw-bg-iron-950"><NFTImage nft={nft} animation={false} height={650} transparentBG={true} showBalance={false} showOriginal={fullscreenOriginalTargetId === "the-art-fullscreen-img"} id="the-art-fullscreen-img" />{printMediaActions()}</div>}{printArtworkControls()}</>}</Row></Container>;
    $[47] = currentSlide;
    $[48] = fullscreenOriginalTargetId;
    $[49] = hasAnimation;
    $[50] = hasImage;
    $[51] = nft;
    $[52] = printArtworkControls;
    $[53] = printMediaActions;
    $[54] = t16;
  } else {
    t16 = $[54];
  }
  return t16;
}
function _temp2(slide_0) {
  return Math.min(1, slide_0 + 1);
}
function _temp(slide) {
  return Math.max(0, slide - 1);
}